import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/components/DashboardLayout";
import { workerNavItems, teamLeaderNavItems } from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Building2,
  MapPin,
  Calendar,
  Users,
  DollarSign,
  FileSignature,
  CheckCircle,
  Shield,
  AlertTriangle,
  Loader2,
  FileText,
  User,
  Download,
  Percent
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ViewSignedContract() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { t } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Determine nav items based on user role
  const navItems = user?.role === "team_leader" ? teamLeaderNavItems : workerNavItems;
  const basePath = user?.role === "team_leader" ? "/team-leader" : "/worker";

  // PPE emoji mapping
  const ppeEmojis = {
    helmet: "⛑️",
    safety_shoes: "👢",
    hi_vis: "🦺",
    harness: "🪢",
    gloves: "🧤",
    ear_protection: "🎧",
    safety_glasses: "🥽",
    dust_mask: "😷",
    face_shield: "🛡️",
    knee_pads: "🦵",
    respirator: "😮‍💨",
    safety_harness: "🪢"
  };

  // Hazard label mapping
  const hazardLabels = {
    fall_height: "Fall fra høyde",
    falling_objects: "Fallende gjenstander",
    electrical: "Elektrisk fare",
    chemical: "Kjemisk fare",
    noise: "Støy",
    dust: "Støv",
    sharp_edges: "Skarpe kanter",
    heavy_lifting: "Tunge løft",
    confined_space: "Trang plass",
    weather: "Værforhold"
  };

  useEffect(() => {
    fetchContractData();
  }, [projectId, token]);

  const fetchContractData = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/projects-v3/team-contract/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (error) {
      console.error("Error fetching contract:", error);
      toast.error(t('common.error') || "Kunne ikke laste kontrakt");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${API_URL}/api/contracts-pdf/team/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error("Failed to download");
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contract-${projectId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(t('common.downloadSuccess') || "Kontrakt lastet ned");
    } catch (error) {
      console.error("Download error:", error);
      toast.error(t('common.downloadError') || "Kunne ikke laste ned kontrakt");
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "TBD";
    return new Date(dateStr).toLocaleDateString('nb-NO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <DashboardLayout navItems={navItems} title={t('contracts.viewContract')}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#23F7DD]" />
        </div>
      </DashboardLayout>
    );
  }

  const { project, application, fee_breakdown } = data || {};
  
  // Calculate financial details
  const teamMembersCount = application?.team_members?.length || 0;
  const actualTeamSize = 1 + teamMembersCount;
  const netToTeam = project?.net_to_team || (fee_breakdown?.net_to_team) || 0;
  const baseShare = netToTeam / actualTeamSize;
  
  // Find current user's signature and contribution
  const isTeamLeader = user?.user_id === application?.team_leader_id;
  const mySignature = isTeamLeader 
    ? { signed_at: project?.tl_signed_at, name: project?.tl_signed_by || application?.team_leader_name }
    : project?.worker_signatures?.find(s => s.user_id === user?.user_id);
  
  const myContribution = project?.worker_signatures?.find(s => s.user_id === user?.user_id)?.tl_contribution_percent || 0;
  const myFinalShare = isTeamLeader 
    ? baseShare + (project?.worker_signatures?.reduce((sum, s) => sum + (baseShare * (s.tl_contribution_percent || 0) / 100), 0) || 0)
    : baseShare - (baseShare * myContribution / 100);

  const calculateDuration = () => {
    if (project?.duration_days) return `${project.duration_days} ${t('common.days')}`;
    if (project?.start_date && project?.end_date) {
      const start = new Date(project.start_date);
      const end = new Date(project.end_date);
      const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      return `${days} ${t('common.days')}`;
    }
    return "TBD";
  };

  return (
    <DashboardLayout navItems={navItems} title={t('contracts.viewContract') || 'Se Kontrakt'}>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-['Unbounded'] font-bold text-white">
                {t('contracts.teamContract') || 'Teamkontrakt'}
              </h1>
              <p className="text-gray-400 mt-1">{project?.title}</p>
            </div>
          </div>
          
          <Button
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            {t('common.download') || 'Last ned'} PDF
          </Button>
        </div>

        {/* Contract Status */}
        <Card className="bg-green-500/10 border-green-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-400" />
                <div>
                  <p className="text-white font-medium">{t('contracts.contractSigned') || 'Kontrakt signert'}</p>
                  {mySignature && (
                    <p className="text-green-400 text-sm">
                      {t('contracts.signedBy') || 'Signert av'} {mySignature.name} - {formatDate(mySignature.signed_at)}
                    </p>
                  )}
                </div>
              </div>
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                <CheckCircle className="w-3 h-3 mr-1" />
                {t('status.signed') || 'Signert'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Project Info */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#23F7DD]" />
              {t('projectDetail.projectDetails') || 'Prosjektdetaljer'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-gray-500 text-sm">{t('projects.project') || 'Prosjekt'}</p>
                <p className="text-white font-semibold">{project?.title}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">{t('projects.location') || 'Lokasjon'}</p>
                <p className="text-white flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-[#23F7DD]" />
                  {project?.location_city}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">{t('projects.startDate') || 'Startdato'}</p>
                <p className="text-white flex items-center gap-1">
                  <Calendar className="w-4 h-4 text-green-400" />
                  {formatDate(project?.start_date)}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">{t('projects.endDate') || 'Sluttdato'}</p>
                <p className="text-white flex items-center gap-1">
                  <Calendar className="w-4 h-4 text-red-400" />
                  {formatDate(project?.end_date)}
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <p className="text-gray-500 text-sm">{t('projects.duration') || 'Varighet'}</p>
                <p className="text-white font-semibold">{calculateDuration()}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">{t('projects.projectType') || 'Prosjekttype'}</p>
                <p className="text-white capitalize">{project?.project_type?.replace("_", " ") || "Stillastjenester"}</p>
              </div>
            </div>
            
            {project?.description && (
              <div className="mt-4 p-4 bg-white/5 rounded-lg">
                <p className="text-gray-500 text-sm mb-2">{t('projects.description') || 'Beskrivelse'}</p>
                <p className="text-gray-300">{project.description}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* SJA - Safety Analysis */}
        {project?.sja_form && (
          <Card className="bg-[#0A0A0A] border-orange-500/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-orange-400" />
                Sikker Jobb Analyse (SJA)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Job Description */}
              {project.sja_form.job_description && (
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-gray-500 text-sm mb-1">{t('sja.jobDescription') || 'Jobbeskrivelse'}</p>
                  <p className="text-white">{project.sja_form.job_description}</p>
                </div>
              )}
              
              {/* Emergency Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {project.sja_form.emergency_contact && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-red-400 text-sm mb-1">📞 {t('sja.emergencyContact') || 'Nødkontakt'}</p>
                    <p className="text-white font-bold text-lg">{project.sja_form.emergency_contact}</p>
                  </div>
                )}
                
                {project.sja_form.nearest_hospital && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-red-400 text-sm mb-1">🏥 {t('sja.nearestHospital') || 'Nærmeste sykehus'}</p>
                    <p className="text-white font-semibold">{project.sja_form.nearest_hospital}</p>
                  </div>
                )}
                
                {project.sja_form.first_aid_location && (
                  <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-green-400 text-sm mb-1">🩹 {t('sja.firstAidLocation') || 'Førstehjelpsutstyr'}</p>
                    <p className="text-white font-semibold">{project.sja_form.first_aid_location}</p>
                  </div>
                )}
              </div>
              
              {/* Hazards */}
              {project.sja_form.hazards && project.sja_form.hazards.length > 0 && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 font-semibold mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    ⚠️ {t('sja.identifiedHazards') || 'Identifiserte farer'}
                  </p>
                  <div className="space-y-2">
                    {project.sja_form.hazards.map((hazard, idx) => {
                      const hazardId = typeof hazard === 'string' ? hazard : (hazard.id || `hazard_${idx}`);
                      const hazardLabel = hazardLabels[hazardId] || hazardId.replace(/_/g, ' ');
                      const hazardMeasures = typeof hazard === 'object' ? hazard.measures : null;
                      
                      return (
                        <div key={idx} className="p-3 bg-white/5 rounded-lg">
                          <p className="text-white font-semibold capitalize">⚠️ {hazardLabel}</p>
                          {hazardMeasures && (
                            <p className="text-green-400 text-sm mt-1">✅ {hazardMeasures}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* PPE Required */}
              {project.sja_form.ppe_required && project.sja_form.ppe_required.length > 0 && (
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-blue-400 font-semibold mb-3 flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    🛡️ {t('sja.requiredPPE') || 'Påkrevd verneutstyr'}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {project.sja_form.ppe_required.map((ppe, idx) => {
                      const emoji = ppeEmojis[ppe] || "🔧";
                      return (
                        <div key={idx} className="px-3 py-2 bg-white/10 rounded-lg flex items-center gap-2">
                          <span className="text-xl">{emoji}</span>
                          <span className="text-white capitalize">{ppe.replace(/_/g, ' ')}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Team Info */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-[#23F7DD]" />
              Team
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {/* Team Leader */}
              <div className="flex items-center justify-between p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">{application?.team_leader_name}</p>
                    <p className="text-purple-400 text-sm">Team Leader</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {project?.tl_signed_at ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span className="text-green-400 text-sm">{t('status.signed') || 'Signert'}</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-5 h-5 text-yellow-400" />
                      <span className="text-yellow-400 text-sm">{t('status.pending') || 'Venter'}</span>
                    </>
                  )}
                </div>
              </div>
              
              {/* Workers */}
              {application?.team_members?.map((member, idx) => {
                const isCurrentUser = member.user_id === user?.user_id;
                const hasSigned = project?.worker_signatures?.some(s => s.user_id === member.user_id);
                const memberContrib = project?.worker_signatures?.find(s => s.user_id === member.user_id)?.tl_contribution_percent || 0;
                
                return (
                  <div 
                    key={idx} 
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      isCurrentUser 
                        ? 'bg-[#23F7DD]/10 border-[#23F7DD]/30' 
                        : 'bg-blue-500/10 border-blue-500/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-white font-semibold">{member.name}</p>
                          {member.worker_code && (
                            <Badge variant="outline" className="text-xs border-gray-600 text-gray-400">
                              {member.worker_code}
                            </Badge>
                          )}
                          {isCurrentUser && (
                            <Badge className="bg-[#23F7DD]/20 text-[#23F7DD] text-xs">
                              {t('common.you') || 'Deg'}
                            </Badge>
                          )}
                        </div>
                        <p className="text-blue-400 text-sm">{member.role || 'Arbeider'}</p>
                        {memberContrib > 0 && (
                          <p className="text-purple-400 text-xs flex items-center gap-1 mt-1">
                            <Percent className="w-3 h-3" />
                            {memberContrib}% → TL
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {hasSigned ? (
                        <>
                          <CheckCircle className="w-5 h-5 text-green-400" />
                          <span className="text-green-400 text-sm">{t('status.signed') || 'Signert'}</span>
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="w-5 h-5 text-yellow-400" />
                          <span className="text-yellow-400 text-sm">{t('status.pending') || 'Venter'}</span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Financial Details */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#23F7DD]" />
              {t('contracts.financialTerms') || 'Økonomiske vilkår'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-gray-400 text-sm">{t('contracts.netToTeam') || 'Netto til team'}</p>
                  <p className="text-sm text-gray-500 mb-1">({actualTeamSize} {t('common.members') || 'medlemmer'}, {t('common.beforeTax') || 'før skatt'})</p>
                  <p className="text-2xl font-bold text-green-400">
                    {Math.round(netToTeam).toLocaleString()} kr
                  </p>
                </div>
                <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <p className="text-gray-400 text-sm">{t('contracts.yourBaseShare') || 'Din grunnandel'}</p>
                  <p className="text-sm text-gray-500 mb-1">({t('common.beforeTax') || 'før skatt'})</p>
                  <p className="text-2xl font-bold text-purple-400">
                    {Math.round(baseShare).toLocaleString()} kr
                  </p>
                </div>
                <div className="p-4 bg-[#23F7DD]/10 border border-[#23F7DD]/30 rounded-lg">
                  <p className="text-gray-400 text-sm">{t('contracts.yourFinalShare') || 'Din endelige andel'}</p>
                  <p className="text-sm text-gray-500 mb-1">
                    {isTeamLeader ? '(inkl. bonus)' : myContribution > 0 ? `(-${myContribution}% TL)` : ''}
                  </p>
                  <p className="text-2xl font-bold text-[#23F7DD]">
                    {Math.round(myFinalShare).toLocaleString()} kr
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contract Terms */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#23F7DD]" />
              {t('contracts.contractTerms') || 'Kontraktsvilkår'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-gray-300">
              <ul className="list-disc pl-6 space-y-2">
                <li>{t('contracts.term1') || 'Å fullføre stillasarbeidet som spesifisert i prosjektkravene'}</li>
                <li>{t('contracts.term2') || 'Å følge alle sikkerhetsforskrifter og AllStillas retningslinjer'}</li>
                <li>{t('contracts.term3') || 'Å gi daglige fremdriftsoppdateringer under prosjektet'}</li>
                <li>{t('contracts.term4') || 'Å fullføre arbeidet innen den angitte tidsrammen'}</li>
                <li>{t('contracts.term5') || 'Å akseptere de økonomiske vilkårene som beskrevet ovenfor'}</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Signatures */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-[#23F7DD]" />
              {t('contracts.signatures') || 'Signaturer'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* TL Signature */}
              {project?.tl_signed_at && (
                <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-purple-400 font-medium">Team Leader</span>
                    <Badge className="bg-green-500/20 text-green-400">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {t('status.signed') || 'Signert'}
                    </Badge>
                  </div>
                  <p className="text-white">{project.tl_signed_by || application?.team_leader_name}</p>
                  <p className="text-gray-400 text-sm">{formatDate(project.tl_signed_at)}</p>
                </div>
              )}
              
              {/* Worker Signatures */}
              {project?.worker_signatures?.map((sig, idx) => (
                <div key={idx} className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-blue-400 font-medium">Arbeider</span>
                    <Badge className="bg-green-500/20 text-green-400">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {t('status.signed') || 'Signert'}
                    </Badge>
                  </div>
                  <p className="text-white">{sig.name}</p>
                  <p className="text-gray-400 text-sm">{formatDate(sig.signed_at)}</p>
                  {sig.tl_contribution_percent > 0 && (
                    <p className="text-purple-400 text-sm mt-1">
                      TL bidrag: {sig.tl_contribution_percent}%
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Download Button at Bottom */}
        <div className="flex justify-center pb-8">
          <Button
            onClick={handleDownloadPDF}
            disabled={downloading}
            size="lg"
            className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black px-8"
          >
            {downloading ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <Download className="w-5 h-5 mr-2" />
            )}
            {t('common.download') || 'Last ned'} PDF
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
